import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createAuditCalendar,
	deleteAuditCalendar,
	fetchAuditCalendars,
	fetchAuditCalendarStats,
	fetchInstitutesByDistrict,
	fetchSchoolCategories,
	updateAuditCalendar,
} from "../api/auditCalendar.api";
import { AuditCalendarPayload } from "../types/auditCalendar.type";

export const useSchoolCategories = () => {
	const { data, isLoading } = useQuery({
		queryKey: ["school-categories"],
		queryFn: fetchSchoolCategories,
		staleTime: 5 * 60 * 1000,
	});

	return { schoolCategories: data || [], isLoading };
};

export const useInstitutesByDistrict = (districtId: number | string | null) => {
	const { data, isLoading } = useQuery({
		queryKey: ["institutes-by-district", districtId],
		queryFn: () => fetchInstitutesByDistrict(districtId!),
		enabled: !!districtId,
	});

	return { institutes: data || [], isLoading };
};

export const AUDIT_CALENDAR_QUERY_KEY = ["audit-calendars-list"];
export const AUDIT_CALENDAR_STATS_QUERY_KEY = ["audit-calendars-stats"];

export const useAuditCalendars = (
	schemeId?: number | string,
	districtId?: number | string,
	search?: string,
) => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: [...AUDIT_CALENDAR_QUERY_KEY, schemeId || "all", districtId || "all", search || ""],
		queryFn: () => fetchAuditCalendars(schemeId, districtId, search),
	});

	return {
		auditCalendars: data || [],
		isLoading,
		error,
		refetch,
	};
};

export const useAuditCalendarStats = (
	schemeId?: number | string,
	districtId?: number | string,
) => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: [...AUDIT_CALENDAR_STATS_QUERY_KEY, schemeId || "all", districtId || "all"],
		queryFn: () => fetchAuditCalendarStats(schemeId, districtId),
	});

	return {
		stats: data || {
			totalPlanned: 0,
			completedAudits: 0,
			auditingThisWeek: 0,
			uploadedInMis: 0,
			nirnayYes: 0,
		},
		isLoading,
		error,
		refetch,
	};
};

export const useCreateAuditCalendar = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: AuditCalendarPayload) => createAuditCalendar(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_STATS_QUERY_KEY });
		},
	});
};

export const useUpdateAuditCalendar = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: number | string;
			payload: Partial<AuditCalendarPayload>;
		}) => updateAuditCalendar(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_STATS_QUERY_KEY });
		},
	});
};

export const useDeleteAuditCalendar = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number | string) => deleteAuditCalendar(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_QUERY_KEY });
			queryClient.invalidateQueries({ queryKey: AUDIT_CALENDAR_STATS_QUERY_KEY });
		},
	});
};
